// src/lib/moneyMovement.js
//
// Chunk 7 follow-up — shared Money Movement occurrence + paid-status logic,
// used by both Home's "Money to move" section and the /money-movement sub-page
// so the two surfaces filter identically.
//
// A bill is "paid for a period" if a matched transaction (transactions.bill_id =
// bill.id) exists in the same calendar month as that occurrence's due date. Such
// occurrences are skipped; Money Movement shows the earliest UNPAID occurrence
// within the horizon (or nothing, if all occurrences in the window are paid).
//
const DAY_MS = 24 * 60 * 60 * 1000
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

// Days from today to the next occurrence of `dueDay` (wrap-aware).
export function daysUntil(dueDay, today) {
  const day = today.getDate()
  if (dueDay >= day) return dueDay - day
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  return lastDay - day + dueDay
}

// Build a Set of "<bill_id>:<year>-<month>" from matched transactions (rows with
// bill_id + transaction_date). Membership means that bill's occurrence in that
// calendar month already has a matching payment.
export function buildPaidMonthSet(matchedTxns) {
  const set = new Set()
  ;(matchedTxns || []).forEach(t => {
    if (!t.bill_id || !t.transaction_date) return
    const d = new Date(t.transaction_date + 'T12:00:00')
    set.add(`${t.bill_id}:${d.getFullYear()}-${d.getMonth() + 1}`)
  })
  return set
}

// A bill's upcoming due-date occurrences within `horizonDays` (chronological).
// Each: { offset (days from today), date, ym: "<year>-<month>" }.
export function billOccurrences(bill, today, horizonDays) {
  const off0 = daysUntil(bill.due_day, today)
  const first = new Date(today.getFullYear(), today.getMonth(), today.getDate() + off0)
  const out = []
  let y = first.getFullYear()
  let m = first.getMonth()
  for (let k = 0; k < 4; k++) {
    const lastDay = new Date(y, m + 1, 0).getDate()
    const day = Math.min(bill.due_day, lastDay)
    const date = new Date(y, m, day)
    const offset = Math.round((startOfDay(date) - startOfDay(today)) / DAY_MS)
    if (offset > horizonDays) break
    if (offset >= 0) out.push({ offset, date, ym: `${y}-${m + 1}` })
    m += 1
    if (m > 11) { m = 0; y += 1 }
  }
  return out
}

// The earliest unpaid occurrence within the horizon, or null if every occurrence
// in the window is already paid (or none falls in the window).
export function resolveUnpaidOccurrence(bill, today, horizonDays, paidMonthSet) {
  for (const occ of billOccurrences(bill, today, horizonDays)) {
    if (!paidMonthSet.has(`${bill.id}:${occ.ym}`)) return occ
  }
  return null
}
